import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiDownload, FiPrinter, FiRotateCcw, FiSearch } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import PageLayout from '../../components/PageLayout';
import {
  FiltersCard, FilterGrid, FilterItem, FilterLabel, FilterSelect, FilterInputWrap, ResetBtn,
  KPIGrid, KPI, KL, KV, ToolbarRow, ExportBtn, ReportCard, RTable, Loading, Empty, Pill,
} from './reportStyles';
import { loadAssetReportData, exportRowsToExcel, printAssetReport } from '../../utils/assetReportHelper';
import { appreciationOf, hasAppreciation } from '../../utils/depreciationHelper';

const STATUS_COLORS = {
  Active: colors.success.main, FullyDepreciated: colors.info.main,
  Disposed: colors.text.disabled, Draft: colors.warning.main,
};

const AssetRegisterReport = () => {
  const { activeCompany, activeBranch } = useAuth();
  const { formatCurrency } = useCurrency();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const data = await loadAssetReportData(activeCompany.id, activeBranch);
      setAssets(data.assets);
      setCategories(data.categories);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [activeCompany, activeBranch]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => assets.filter(a => {
    if (categoryFilter !== 'all' && a.categoryId !== categoryFilter) return false;
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!((a.assetName || '').toLowerCase().includes(q) || (a.assetCode || '').toLowerCase().includes(q) || (a.serialOrTagNo || '').toLowerCase().includes(q))) return false;
    }
    return true;
  }).sort((a, b) => (a.assetCode || '').localeCompare(b.assetCode || '')), [assets, categoryFilter, statusFilter, search]);

  const kpis = useMemo(() => {
    let cost = 0, appr = 0, accum = 0, nbv = 0;
    filtered.forEach(a => {
      cost += Number(a.acquisitionCost) || 0;
      appr += appreciationOf(a);
      accum += Number(a.accumulatedDepreciation) || 0;
      nbv += a.status === 'Disposed' ? 0 : (Number(a.netBookValue) || 0);
    });
    return { count: filtered.length, cost, appr, accum, nbv };
  }, [filtered]);
  // Asset Appreciation column only for companies that have any.
  const showAppr = useMemo(() => assets.some(hasAppreciation), [assets]);

  const columns = [
    { key: 'Code', label: 'Code' }, { key: 'Asset', label: 'Asset' }, { key: 'Category', label: 'Category' },
    { key: 'Location', label: 'Location' }, { key: 'Acquired', label: 'Acquired' },
    { key: 'Cost', label: 'Cost', r: true },
    ...(showAppr ? [{ key: 'Appreciation', label: 'Appreciation', r: true }] : []),
    { key: 'AccumDep', label: 'Accum. Dep.', r: true },
    { key: 'NBV', label: 'NBV', r: true }, { key: 'Status', label: 'Status' },
  ];
  const buildRows = () => filtered.map(a => ({
    Code: a.assetCode, Asset: a.assetName, Category: a.categoryName, Location: a.locationName || '',
    Acquired: a.acquisitionDate || '',
    Cost: formatCurrency(a.acquisitionCost || 0),
    ...(showAppr ? { Appreciation: formatCurrency(appreciationOf(a)) } : {}),
    AccumDep: formatCurrency(a.accumulatedDepreciation || 0),
    NBV: formatCurrency(a.status === 'Disposed' ? 0 : (a.netBookValue ?? a.acquisitionCost ?? 0)),
    Status: a.status,
  }));

  const filtersText = [
    categoryFilter !== 'all' ? `Category: ${categories.find(c => c.id === categoryFilter)?.categoryName || ''}` : null,
    statusFilter !== 'all' ? `Status: ${statusFilter}` : null,
    search ? `Search: ${search}` : null,
    activeBranch && activeBranch.id !== 'all' ? `Branch: ${activeBranch.branchName}` : null,
  ].filter(Boolean).join(' | ');

  const onPrint = () => printAssetReport({
    company: activeCompany, branch: activeBranch && activeBranch.id !== 'all' ? activeBranch : null,
    title: 'Fixed Asset Register', filters: filtersText, columns, rows: buildRows(),
    totals: { Cost: formatCurrency(kpis.cost), ...(showAppr ? { Appreciation: formatCurrency(kpis.appr) } : {}), AccumDep: formatCurrency(kpis.accum), NBV: formatCurrency(kpis.nbv) },
    kpis: [
      { label: 'Assets', value: String(kpis.count) },
      { label: 'Cost', value: formatCurrency(kpis.cost) },
      ...(showAppr ? [{ label: 'Appreciation', value: formatCurrency(kpis.appr) }] : []),
      { label: 'Accum. Dep.', value: formatCurrency(kpis.accum) },
      { label: 'Net Book Value', value: formatCurrency(kpis.nbv), highlight: true },
    ],
  });

  return (
    <PageLayout pageTitle="Fixed Asset Register" breadcrumb="Reports / Fixed Assets / Register">
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Category</FilterLabel>
            <FilterSelect value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.categoryName}</option>)}
            </FilterSelect>
          </FilterItem>
          <FilterItem>
            <FilterLabel>Status</FilterLabel>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="Active">Active</option>
              <option value="FullyDepreciated">Fully Depreciated</option>
              <option value="Disposed">Disposed</option>
            </FilterSelect>
          </FilterItem>
          <FilterItem style={{ minWidth: 220 }}>
            <FilterLabel>Search</FilterLabel>
            <FilterInputWrap>
              <FiSearch />
              <input placeholder="Code, name, tag..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </FilterInputWrap>
          </FilterItem>
          <FilterItem>
            <FilterLabel>&nbsp;</FilterLabel>
            <ResetBtn onClick={() => { setCategoryFilter('all'); setStatusFilter('all'); setSearch(''); }}><FiRotateCcw /> Reset</ResetBtn>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      <KPIGrid>
        <KPI><KL>Assets</KL><KV>{kpis.count}</KV></KPI>
        <KPI><KL>Acquisition Cost</KL><KV>{formatCurrency(kpis.cost)}</KV></KPI>
        {showAppr && <KPI><KL>Appreciation</KL><KV>{formatCurrency(kpis.appr)}</KV></KPI>}
        <KPI><KL>Accumulated Depreciation</KL><KV>{formatCurrency(kpis.accum)}</KV></KPI>
        <KPI $highlight><KL>Net Book Value</KL><KV>{formatCurrency(kpis.nbv)}</KV></KPI>
      </KPIGrid>

      <ToolbarRow>
        <h3 style={{ color: colors.text.secondary }}>{filtered.length} asset{filtered.length !== 1 ? 's' : ''}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportBtn onClick={() => exportRowsToExcel('AssetRegister', 'Assets', buildRows())}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={onPrint}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? <Loading>Loading...</Loading> : filtered.length === 0 ? <Empty>No assets match your filters.</Empty> : (
        <ReportCard>
          <RTable>
            <thead>
              <tr>
                <th>Code</th><th>Asset</th><th>Category</th><th>Location</th><th>Acquired</th>
                <th className="r">Cost</th>{showAppr && <th className="r">Appreciation</th>}<th className="r">Accum. Dep.</th><th className="r">NBV</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a.id}>
                  <td><strong>{a.assetCode}</strong></td>
                  <td>{a.assetName}</td>
                  <td>{a.categoryName}</td>
                  <td>{a.locationName || '—'}</td>
                  <td>{a.acquisitionDate}</td>
                  <td className="r">{formatCurrency(a.acquisitionCost || 0)}</td>
                  {showAppr && <td className="r">{formatCurrency(appreciationOf(a))}</td>}
                  <td className="r">{formatCurrency(a.accumulatedDepreciation || 0)}</td>
                  <td className="r">{formatCurrency(a.status === 'Disposed' ? 0 : (a.netBookValue ?? a.acquisitionCost ?? 0))}</td>
                  <td><Pill $color={STATUS_COLORS[a.status]}>{a.status}</Pill></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5}>Total</td>
                <td className="r">{formatCurrency(kpis.cost)}</td>
                {showAppr && <td className="r">{formatCurrency(kpis.appr)}</td>}
                <td className="r">{formatCurrency(kpis.accum)}</td>
                <td className="r">{formatCurrency(kpis.nbv)}</td>
                <td></td>
              </tr>
            </tfoot>
          </RTable>
        </ReportCard>
      )}
    </PageLayout>
  );
};

export default AssetRegisterReport;
